import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../../../../redux/store";
import {
  fetchAttendeesForUser,
  deleteAttendee,
  fetchClasses,
} from "../../../../../redux/admin/adminOperations";
import { Attendee } from "../../../../../redux/types/types";
import Notiflix from "notiflix";
import EditAttendeeForm from "./EditAttendeeForm/EditAttendeeForm";
import CreateAttendeeForm from "./CreateAttendeeForm/CreateAttendeeForm";
import SpecialClassAccessDisplay from "../../../../Attendees/SpecialClassAccessDisplay";
import AdjustClassesDialog from "./AdjustClassesDialog/AdjustClassesDialog";
import {
  getAttendeeEnrolledClassIdsApi,
  getAttendeeImportantInfoApi,
} from "../../../../../utils/api/attendees";
import { shouldShowClassCounters } from "../../../../../utils/specialClassAccess";
import AttendeeImportantInfoButton from "../../../../Attendees/AttendeeImportantInfoButton";

interface AttendeesListProps {
  userId: number;
}

const calculateAge = (birthdate: Date) => {
  const today = new Date();
  const birthDate = new Date(birthdate);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export default function AttendeesList({ userId }: AttendeesListProps) {
  const dispatch = useDispatch<AppDispatch>();
  const intl = useIntl();
  const attendees = useSelector((state: RootState) => state.admin.attendees);
  const classes = useSelector((state: RootState) => state.admin.classes);
  const user = useSelector((state: RootState) =>
    state.admin.users.find((u) => u.id === userId),
  );
  const [editAttendee, setEditAttendee] = useState<Attendee | null>(null);
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [adjustAttendee, setAdjustAttendee] = useState<Attendee | null>(null);
  const [attendeeEnrolledClasses, setAttendeeEnrolledClasses] = useState<
    Map<number, number[]>
  >(new Map());

  useEffect(() => {
    dispatch(fetchAttendeesForUser(userId));
    dispatch(
      fetchClasses({
        page: 1,
        limit: 10000,
        sortBy: "id",
        sortOrder: "asc",
        search: "",
      }),
    );
  }, [dispatch, userId]);

  // Fetch enrolled class IDs for all attendees
  useEffect(() => {
    const fetchEnrolledClasses = async () => {
      if (!attendees || attendees.length === 0) return;

      const enrolledMap = new Map<number, number[]>();
      for (const attendee of attendees) {
        try {
          const response = await getAttendeeEnrolledClassIdsApi(attendee.id);
          enrolledMap.set(attendee.id, response.data.enrolledClassIds || []);
        } catch (error) {
          console.error(
            `Failed to fetch enrolled classes for attendee ${attendee.id}:`,
            error,
          );
          enrolledMap.set(attendee.id, []);
        }
      }
      setAttendeeEnrolledClasses(enrolledMap);
    };

    fetchEnrolledClasses();
  }, [attendees]);

  const handleEditClick = async (attendee: Attendee) => {
    try {
      const response = await getAttendeeImportantInfoApi(attendee.id);
      setEditAttendee({
        ...attendee,
        importantInfo: response.data.importantInfo,
      });
    } catch (error) {
      console.error("Failed to load important attendee information:", error);
      setEditAttendee(attendee);
    }
  };

  const handleDeleteClick = (attendeeId: number) => {
    Notiflix.Confirm.show(
      intl.formatMessage({ id: "user.attendee.deleteConfTitle" }),
      intl.formatMessage({ id: "user.attendee.deleteConfMessage" }),
      intl.formatMessage({ id: "common.delete" }),
      intl.formatMessage({ id: "common.cancel" }),
      () => {
        dispatch(deleteAttendee(attendeeId))
          .unwrap()
          .then(() => {
            dispatch(fetchAttendeesForUser(userId));
          })
          .catch((error) => {
            console.error("Failed to delete attendee:", error);
          });
      },
    );
  };

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setIsCreateFormOpen(true)}
        sx={{ mb: 2 }}
      >
        <FormattedMessage
          id="attendees.createNew"
          defaultMessage="Create New Attendee"
        />
      </Button>
      <Grid container spacing={2}>
        {attendees?.map((attendee) => (
          <Grid item xs={12} sm={6} key={attendee.id}>
            <Card
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="div" gutterBottom>
                  {`${attendee.firstName} ${attendee.lastName}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage id="admin.dashboard.email" />:{" "}
                  {attendee.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage id="admin.dashboard.phone" />:{" "}
                  {attendee.phone}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage id="admin.dashboard.birthdate" />:{" "}
                  {intl.formatDate(attendee.birthdate, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage id="admin.dashboard.age" />:{" "}
                  {calculateAge(attendee.birthdate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage id="admin.dashboard.sex" />:{" "}
                  {attendee.sex === "female"
                    ? intl.formatMessage({
                        id: "privateEnroll.female",
                      })
                    : attendee.sex === "male"
                      ? intl.formatMessage({
                          id: "privateEnroll.male",
                        })
                      : attendee.sex === "other"
                        ? intl.formatMessage({
                            id: "privateEnroll.other",
                          })
                        : attendee.sex}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage id="signUp.postalCode" />:{" "}
                  {attendee.postalCode || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage id="attendee.permisStatus.title" />:{" "}
                  {(attendee.permisStatus === "PermisS" &&
                    intl.formatMessage({
                      id: "attendee.permisStatus.permisS",
                    })) ||
                    (attendee.permisStatus === "Other" &&
                      intl.formatMessage({
                        id: "attendee.permisStatus.other",
                      }))}
                </Typography>
                {shouldShowClassCounters(
                  attendee,
                  attendeeEnrolledClasses.get(attendee.id),
                  classes,
                ) && (
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="attendee.remainingNonSpecialClasses" />
                    : {attendee.remainingClasses}
                  </Typography>
                )}
                <SpecialClassAccessDisplay attendee={attendee} />
                <Box sx={{ mt: 1 }}>
                  <AttendeeImportantInfoButton attendee={attendee} />
                </Box>
              </CardContent>
              <Box sx={{ p: 2, mt: "auto" }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleEditClick(attendee)}
                  sx={{ mr: 1, mb: 1 }}
                >
                  <FormattedMessage id="attendees.edit" defaultMessage="Edit" />
                </Button>
                {shouldShowClassCounters(
                  attendee,
                  attendeeEnrolledClasses.get(attendee.id),
                  classes,
                ) && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    onClick={() => setAdjustAttendee(attendee)}
                    sx={{ mr: 1, mb: 1 }}
                  >
                    <FormattedMessage
                      id="attendees.adjustClasses"
                      defaultMessage="Adjust Classes"
                    />
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => handleDeleteClick(attendee.id)}
                  sx={{ mb: 1 }}
                >
                  <FormattedMessage
                    id="attendees.delete"
                    defaultMessage="Delete"
                  />
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
      {editAttendee && (
        <EditAttendeeForm
          open={!!editAttendee}
          onClose={() => setEditAttendee(null)}
          attendee={editAttendee}
        />
      )}
      {adjustAttendee && (
        <AdjustClassesDialog
          open={!!adjustAttendee}
          onClose={() => setAdjustAttendee(null)}
          attendee={adjustAttendee}
        />
      )}
      {user && (
        <CreateAttendeeForm
          open={isCreateFormOpen}
          onClose={() => setIsCreateFormOpen(false)}
          user={user}
        />
      )}
    </Box>
  );
}
